import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from "../../../config";
import { isAuthenticated, AuthRequired } from './payment';
// ---------------------------------------------------------------------------
// ADVANCE PAYMENT MODULE (self-contained)
// Engineer submits/edits/views own requests.
// Admin / SuperEngineer views pending + all requests and reviews them.
// Usage:  <AdvancePayment currentUser={{ id, name, role, token }} />
// ---------------------------------------------------------------------------

const api = axios.create({
  baseURL: BASE_URL,
});

// FIX: the token was never actually attached before — every request went out
// unauthenticated. Read it fresh from localStorage per-request (same source
// of truth used by Engineer.jsx / EngineerAttendance.jsx / EngineerLeave.jsx),
// rather than trying to capture it at module-load time when no user is known
// yet. If your app centralizes this in a shared axiosInstance already,
// prefer importing that instead of this local `api` — see the note at the
// bottom of this file.
api.interceptors.request.use((config) => {
  try {
    const stored = JSON.parse(localStorage.getItem('NagpurProperties') || '{}');
    if (stored?.token) config.headers.Authorization = `Bearer ${stored.token}`;
  } catch {
    // no stored session — request goes out unauthenticated, backend will 401
  }
  return config;
});

const REASON_TYPES = ['MEDICAL_EMERGENCY', 'FAMILY_EMERGENCY', 'EDUCATION', 'TRAVEL', 'OTHER'];
const ADVANCE_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'PARTIALLY_RECOVERED', 'FULLY_RECOVERED'];
const PAYMENT_TYPES = ['CASH', 'BANK_TRANSFER', 'UPI', 'CHEQUE'];

// FIX: was a plain array + exact-match .includes(), which silently breaks if
// this component is ever used standalone with a raw (unnormalized) role
// string like "SuperEngineer" instead of the canonical "SUPER_ENGINEER" that
// Payments.jsx happens to produce. Normalize before comparing so this module
// is safe whether or not it's wrapped by Payments.jsx.
const REVIEWER_ROLE_VARIANTS = ['ADMIN', 'SUPERENGINEER', 'SUPER_ENGINEER', 'SUPER ENGINEER'];
function isReviewerRole(role) {
  return REVIEWER_ROLE_VARIANTS.includes(String(role || '').toUpperCase());
}

const submitAdvanceRequest = (engineerId, payload) =>
  api.post(`/engineer/${engineerId}/submit/advance-request`, payload).then((r) => r.data);

const reviewAdvanceRequest = (advanceId, reviewerId, reviewerRole, payload) =>
  api
    .put(`/review/engineer/advance-request/${advanceId}`, payload, { params: { reviewerId, reviewerRole } })
    .then((r) => r.data);

const getPendingAdvanceRequests = (params) =>
  api.get('/pending/advance-request', { params }).then((r) => r.data);

const getAllAdvanceRequests = (params) =>
  api.get('/engineers/advances-request', { params }).then((r) => r.data);

const getEngineerAdvanceRequests = (engineerId, params) =>
  api.get(`/engineer/${engineerId}/advance-request`, { params }).then((r) => r.data);

// ---------------------------------------------------------------------------
// UI: Submit form (Engineer)
// ---------------------------------------------------------------------------
function AdvanceRequestForm({ engineerId, onSubmitted }) {
  const [form, setForm] = useState({ requestedAmount: '', reasonType: REASON_TYPES[0], additionalReason: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.requestedAmount || Number(form.requestedAmount) <= 0) {
      setError('Enter a valid amount.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await submitAdvanceRequest(engineerId, { ...form, requestedAmount: Number(form.requestedAmount) });
      setForm({ requestedAmount: '', reasonType: REASON_TYPES[0], additionalReason: '' });
      onSubmitted?.();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <h3>Request Advance</h3>
      <label style={styles.label}>
        Amount
        <input type="number" name="requestedAmount" min="1" value={form.requestedAmount} onChange={handleChange} style={styles.input} required />
      </label>
      <label style={styles.label}>
        Reason
        <select name="reasonType" value={form.reasonType} onChange={handleChange} style={styles.input}>
          {REASON_TYPES.map((r) => (
            <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </label>
      <label style={styles.label}>
        Additional details
        <textarea name="additionalReason" value={form.additionalReason} onChange={handleChange} rows={3} style={styles.input} />
      </label>
      {error && <p style={styles.error}>{error}</p>}
      <button type="submit" disabled={submitting} style={styles.btnPrimary}>
        {submitting ? 'Submitting...' : 'Submit Request'}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// UI: Review modal (Admin / SuperEngineer)
// ---------------------------------------------------------------------------
function AdvanceReviewModal({ advance, currentUser, onClose, onReviewed }) {
  const [form, setForm] = useState({
    advanceStatus: 'APPROVED',
    approveAmount: advance.requestedAmount,
    paymentType: PAYMENT_TYPES[0],
    paymentReferenceNo: '',
    paymentRemark: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await reviewAdvanceRequest(advance.id, currentUser.id, currentUser.role, {
        ...form,
        approveAmount: Number(form.approveAmount),
      });
      onReviewed?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3>Review Advance #{advance.id}</h3>
        <p style={styles.subtitle}>{advance.engineerName} · Requested ₹{advance.requestedAmount}</p>

        <label style={styles.label}>
          Decision
          <select name="advanceStatus" value={form.advanceStatus} onChange={handleChange} style={styles.input}>
            {ADVANCE_STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </label>
        <label style={styles.label}>
          Approved amount
          <input type="number" name="approveAmount" value={form.approveAmount} onChange={handleChange} style={styles.input} />
        </label>
        <label style={styles.label}>
          Payment type
          <select name="paymentType" value={form.paymentType} onChange={handleChange} style={styles.input}>
            {PAYMENT_TYPES.map((p) => (
              <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </label>
        <label style={styles.label}>
          Payment reference no.
          <input type="text" name="paymentReferenceNo" value={form.paymentReferenceNo} onChange={handleChange} style={styles.input} />
        </label>
        <label style={styles.label}>
          Remark
          <textarea name="paymentRemark" value={form.paymentRemark} onChange={handleChange} rows={2} style={styles.input} />
        </label>

        {error && <p style={styles.error}>{error}</p>}

        <div style={styles.modalActions}>
          <button onClick={onClose} disabled={saving} style={styles.btnSecondary}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={styles.btnPrimary}>
            {saving ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UI: List (shared by engineer-own-history and reviewer views)
// searchTerm is applied CLIENT-SIDE, against whatever page of data is
// currently loaded — it does not go to the backend and does not affect
// pagination. See note in AdvancePayment if you need it to search across
// the whole dataset instead of just the current page.
// ---------------------------------------------------------------------------
function AdvanceRequestList({ fetchFn, filters, currentUser, reviewable, searchTerm = '' }) {
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const filterKey = JSON.stringify(filters);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn({ ...filters, page, size: 10 });
      setData(result);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFn, filterKey, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [filterKey]);

  if (loading) return <p style={styles.muted}>Loading...</p>;
  if (error) return <p style={styles.error}>{error}</p>;

  const rawItems = data?.content || [];
  const trimmedSearch = searchTerm.trim().toLowerCase();
  const items = trimmedSearch
    ? rawItems.filter((row) => row.engineerName?.toLowerCase().includes(trimmedSearch))
    : rawItems;

  if (!items.length) return <p style={styles.muted}>No advance requests found.</p>;

  return (
    <div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Engineer</th>
            <th style={styles.th}>Requested</th>
            <th style={styles.th}>Approved</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Date</th>
            {reviewable && <th style={styles.th}>Action</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr key={row.id}>
              <td style={styles.td}>{row.id}</td>
              <td style={styles.td}>{row.engineerName}</td>
              <td style={styles.td}>₹{row.requestedAmount}</td>
              <td style={styles.td}>₹{row.approvedAmount ?? '-'}</td>
              <td style={styles.td}>{row.advanceStatus}</td>
              <td style={styles.td}>{row.requestedDate}</td>
              {reviewable && isReviewerRole(currentUser?.role) && (
                <td style={styles.td}>
                  <button style={styles.linkBtn} onClick={() => setSelected(row)}>Review</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {data && data.totalPages > 1 && (
        <div style={styles.pagination}>
          <button disabled={!data.hasPrevious} onClick={() => setPage((p) => p - 1)} style={styles.btnSecondary}>Prev</button>
          <span style={styles.muted}>Page {data.currentPage + 1} of {data.totalPages}</span>
          <button disabled={!data.hasNext} onClick={() => setPage((p) => p + 1)} style={styles.btnSecondary}>Next</button>
        </div>
      )}

      {selected && (
        <AdvanceReviewModal
          advance={selected}
          currentUser={currentUser}
          onClose={() => setSelected(null)}
          onReviewed={load}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root export
// ---------------------------------------------------------------------------
export default function AdvancePayment({ currentUser }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState('pending'); // reviewer only: pending | all
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const isReviewer = isReviewerRole(currentUser?.role);

  if (!isAuthenticated(currentUser)) return <AuthRequired />;

  if (isReviewer) {
    const fetchFn = tab === 'pending' ? getPendingAdvanceRequests : getAllAdvanceRequests;
    return (
      <div>
        <div style={styles.tabRow}>
          <button style={tab === 'pending' ? styles.tabActive : styles.tab} onClick={() => setTab('pending')}>Pending</button>
          <button style={tab === 'all' ? styles.tabActive : styles.tab} onClick={() => setTab('all')}>All Requests</button>
        </div>
        {tab === 'all' && (
          <div style={styles.filterRow}>
            <input
              type="text"
              placeholder="Search engineer name"
              style={styles.input}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select style={styles.input} defaultValue=""
              onChange={(e) => setFilters((f) => ({ ...f, advanceStatus: e.target.value || undefined }))}>
              <option value="">All statuses</option>
              {ADVANCE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="date" style={styles.input}
              onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value || undefined }))} />
            <input type="date" style={styles.input}
              onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value || undefined }))} />
          </div>
        )}
        <AdvanceRequestList
          fetchFn={fetchFn}
          filters={tab === 'all' ? filters : {}}
          currentUser={currentUser}
          reviewable
          searchTerm={tab === 'all' ? searchTerm : ''}
        />
      </div>
    );
  }

  // Engineer view: submit + own history only — deliberately never calls
  // getAllAdvanceRequests / getPendingAdvanceRequests, so a plain engineer
  // has no code path to another engineer's advance data even if they poked
  // at devtools state.
  const fetchFn = (params) => getEngineerAdvanceRequests(currentUser.id, params);
  return (
    <div>
      <AdvanceRequestForm engineerId={currentUser.id} onSubmitted={() => setRefreshKey((k) => k + 1)} />
      <h3>My Advance Request History</h3>
      <AdvanceRequestList key={refreshKey} fetchFn={fetchFn} filters={{}} currentUser={currentUser} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Minimal inline styles (self-contained, no extra CSS file)
// ---------------------------------------------------------------------------
const styles = {
  form: { maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 12, background: '#fff', padding: 20, borderRadius: 10, border: '1px solid #eee', marginBottom: 24 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#444' },
  input: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, fontWeight: 400 },
  btnPrimary: { padding: '8px 16px', border: 'none', borderRadius: 6, background: '#2d5bd7', color: '#fff', fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', color: '#333', fontWeight: 600, cursor: 'pointer' },
  linkBtn: { background: 'none', border: 'none', color: '#2d5bd7', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 13 },
  error: { color: '#c0392b', fontSize: 13 },
  muted: { color: '#777', fontSize: 13 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden' },
  th: { padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', background: '#fafafa', fontSize: 13, fontWeight: 600, color: '#555' },
  td: { padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #f0f0f0', fontSize: 13 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', width: 420, maxWidth: '92vw', borderRadius: 10, padding: 24, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '90vh', overflowY: 'auto' },
  subtitle: { color: '#777', fontSize: 13, margin: '-8px 0 4px' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  tabRow: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { padding: '8px 16px', border: '1px solid #ddd', background: '#fff', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#555' },
  tabActive: { padding: '8px 16px', border: '1px solid #2d5bd7', background: '#2d5bd7', color: '#fff', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  filterRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  pagination: { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 4px' },
};